import React from "react";
import ContractDashboard, { fromWei, formatDuration } from "./shared/ContractDashboard";
import stakingFunctions from "./functions/stakingFunctions";
import { nemiStaking, nemiStakingAbi } from "./Contract";

/**
 * Nemi Staking — lock NEMI, accrue the daily reward and claim it per position.
 * Every function of the nemiStaking contract is available through the tabs below.
 */
export default function StakingPage() {
  const overview = [
    {
      key: "myStaked",
      label: "Your Staked NEMI",
      icon: "🔒",
      color: "#00ff88",
      format: (v) => `${v} NEMI`,
      compute: async ({ contract, account }) => {
        const stakes = await contract.methods.getUserStakes(account).call();
        const total = (stakes || []).reduce(
          (sum, s) => sum + (s.closed ? BigInt(0) : BigInt(s.amount ?? s[0])),
          BigInt(0)
        );
        return fromWei(total.toString());
      },
    },
    {
      key: "myPending",
      label: "Your Pending Reward",
      icon: "🎁",
      color: "#ffaa00",
      format: (v) => `${v} NEMI`,
      compute: async ({ contract, account }) => {
        const count = Number(await contract.methods.getUserStakeCount(account).call());
        if (count === 0) return "0";
        const pendings = await Promise.all(
          Array.from({ length: count }, (_, i) =>
            contract.methods.pendingReward(account, i).call().catch(() => "0")
          )
        );
        const total = pendings.reduce((sum, p) => sum + BigInt(p), BigInt(0));
        return fromWei(total.toString());
      },
    },
    {
      key: "myPositions",
      label: "Your Positions",
      icon: "📋",
      color: "#45b7d1",
      compute: async ({ contract, account }) =>
        String(await contract.methods.getUserStakeCount(account).call()),
    },
    {
      key: "myNemiBalance",
      label: "Your NEMI Balance",
      icon: "🪙",
      color: "#00ccff",
      format: (v) => `${v} NEMI`,
      compute: async ({ getTokenContract, account }) =>
        fromWei(await getTokenContract("NEMI").methods.balanceOf(account).call()),
    },
    {
      key: "totalStaked",
      label: "Total Staked",
      icon: "🏦",
      method: "totalStaked",
      format: (v) => `${fromWei(v)} NEMI`,
      color: "#ff00aa",
    },
    {
      key: "totalClaimed",
      label: "Total Claimed",
      icon: "💰",
      method: "totalClaimed",
      format: (v) => `${fromWei(v)} NEMI`,
      color: "#6be0d7",
    },
    {
      key: "isStakingOpen",
      label: "Staking Window",
      icon: "🚪",
      method: "isStakingOpen",
      format: (v) => (v === true || v === "true" ? "OPEN" : "CLOSED"),
      color: "#00ff88",
    },
    {
      key: "globalCycle",
      label: "Global Cycle",
      icon: "🔄",
      method: "globalCycle",
      color: "#ffaa00",
    },
    {
      key: "rewardInterval",
      label: "Reward Interval",
      icon: "⏱️",
      method: "rewardInterval",
      format: (v) => formatDuration(v),
      color: "#45b7d1",
    },
    {
      key: "rewardPerStakedToken",
      label: "Reward / Staked Token",
      icon: "📊",
      method: "rewardPerStakedToken",
      format: (v) => fromWei(v),
      color: "#ff8800",
    },
    {
      key: "maxClaimAmount",
      label: "Max Claim Amount",
      icon: "🧾",
      method: "maxClaimAmount",
      format: (v) => `${fromWei(v)} NEMI`,
      color: "#ff00aa",
    },
    {
      key: "stakePriceThreshold",
      label: "Stake Price Threshold",
      icon: "⚖️",
      method: "stakePriceThreshold",
      format: (v) => fromWei(v),
      color: "#6be0d7",
    },
    {
      key: "contractNemi",
      label: "Contract NEMI",
      icon: "🏛️",
      color: "#00ccff",
      format: (v) => `${v} NEMI`,
      compute: async ({ getTokenContract, address }) =>
        fromWei(await getTokenContract("NEMI").methods.balanceOf(address).call()),
    },
    {
      key: "paused",
      label: "Contract Status",
      icon: "⚡",
      method: "paused",
      format: (v) => (v === true || v === "true" ? "PAUSED" : "ACTIVE"),
      color: "#ff6b6b",
    },
  ];

  return (
    <ContractDashboard
      title="Nemi Staking"
      subtitle="Lock NEMI, accrue the daily reward and claim it per position"
      icon="🔒"
      accent="#00ff88"
      address={nemiStaking}
      abi={nemiStakingAbi}
      functions={stakingFunctions}
      overview={overview}
      approvalTokens={["NEMI", "USDT"]}
    />
  );
}
